import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Theme } from '@material-ui/core/styles'
import makeStyles from '@material-ui/core/styles/makeStyles'
import clsx from 'clsx'
import React, { useCallback, useState } from 'react'
import { pxToRem } from '../Utilities/unitUtils'

const setBackground = ({ gradient, backgroundColor }: RoundButtonStyleProps) =>
    backgroundColor
        ? backgroundColor
        : `linear-gradient(90deg, ${gradient.from} 0%, ${gradient.to} 100%)`

const useStyles = makeStyles((theme: Theme) => ({
    roundButtonContainer: {
        position: 'relative',
    },
    roundButton: {
        border: ({
            border,
            borderColor,
            backgroundColor,
            gradient,
        }: RoundButtonStyleProps) =>
            border
                ? border
                : `1px solid ${
                      borderColor || backgroundColor || gradient.from
                  }`,
        borderRadius: (props: RoundButtonStyleProps) => props.borderRadius,
        background: setBackground,
        boxShadow: (props: RoundButtonStyleProps) =>
            props.hasShadow ? '0px 4px 4px rgba(0, 0, 0, 0.25)' : 'none',
        fontSize: pxToRem(16),
        color: (props: RoundButtonStyleProps) => props.color,
        '&:hover': {
            backgroundColor: (props: RoundButtonStyleProps) => {
                if (typeof props.hoverColor === 'boolean') {
                    return '#d5d5d5'
                }
                if (typeof props.hoverColor === 'string') {
                    return props.hoverColor
                }
                return setBackground(props)
            },
        },
        '& .MuiButton-endIcon': {
            marginLeft: 4,
        },
        '& .MuiButton-startIcon': {
            marginRight: 4,
        },
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    loadingProgress: {
        position: 'absolute',
        left: 0,
        color: ({ color }: RoundButtonStyleProps) => color,
    },
}))

export interface RoundButtonStyleProps {
    /** Show or hide button shadow */
    hasShadow: boolean

    /** Custom gradient background colors
     *
     *  The value for the `from` property is also used
     *  to set the button's border color if not value is provided
     *  for `borderColor`.  This will be overridden by backgroundColor when
     *  it is set.  The borderColor will reflect the same color that was set
     *  to the backgroundColor
     */
    gradient: {
        from: string
        to: string
    }

    /**
     * Set's the backgroundColor of the button.  When there is a value for this prop,
     * the gradient property will not have an effect.
     */
    backgroundColor?: string

    /** Custom text color */
    color: string

    /** Custom border color
     *  If border color is not set, it will check the backgroundColor first and then
     *  the gradient.from color
     */
    borderColor?: string

    /** Custom radius */
    borderRadius?: number
    /**
     * Custom hover coloer
     * Default is same as backgroundColor
     * Set true if default, set to color if otherwise
     */
    hoverColor?: string | boolean

    /** Custom border
     * If this is set it will ignore the color set on
     * borderColor, backgroundColor, gradient
     */
    border?: string
}

export interface RoundButtonProps extends Partial<RoundButtonStyleProps> {
    /** Event handler for click event */
    onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
    /**
     * Icon that will be added after the children
     */
    endIcon?: React.ReactNode
    /**
     * Icon that will be added before the children
     */
    startIcon?: React.ReactNode
    /**
     * Disables the button
     */
    disabled?: boolean
    /**
     * Hide the actionbutton. Default: false
     */
    hidden?: boolean
    /**
     * Shows a spinner when loading
     */
    loading?: boolean
    /**
     * Button size
     */
    size?: 'small' | 'medium' | 'large'
    /**
     * Disable button elevation
     */
    disableElevation?: boolean
}

const RoundButton: React.FC<RoundButtonProps> = ({
    hasShadow = true,
    hidden = false,
    loading = false,
    children = '',
    gradient = {
        from: '#33B55B',
        to: '#98CA48',
    },
    backgroundColor,
    color = '#fff',
    onClick,
    borderColor,
    hoverColor,
    borderRadius = 50,
    endIcon,
    startIcon,
    disabled,
    size = 'small',
    disableElevation = false,
    border,
    ...rest
}) => {
    const [buttonRect, setButtonRect] = useState<DOMRect | null>(null)
    const classes = useStyles({
        hasShadow,
        gradient,
        borderColor,
        backgroundColor,
        color,
        borderRadius,
        hoverColor,
        border,
    })

    const buttonRef = useCallback((node: HTMLButtonElement | null) => {
        if (node) {
            setButtonRect(node.getBoundingClientRect())
        }
    }, [])

    return (
        <div
            className={clsx(
                'roundButtonContainer',
                classes.roundButtonContainer
            )}
            hidden={hidden}
        >
            <Button
                ref={buttonRef}
                onClick={onClick}
                startIcon={startIcon}
                endIcon={endIcon}
                variant="contained"
                disabled={disabled || loading}
                className={clsx('roundButton', classes.roundButton)}
                size={size}
                disableElevation={disableElevation}
                {...rest}
            >
                {children}
            </Button>
            {loading && (
                <CircularProgress
                    size={24}
                    style={{
                        marginLeft: buttonRect
                            ? (buttonRect.width - 24) / 2
                            : 0,
                        marginTop: buttonRect
                            ? (buttonRect.height - 24) / 2
                            : 0,
                    }}
                    className={clsx(
                        'roundButtonLoading',
                        classes.loadingProgress
                    )}
                />
            )}
        </div>
    )
}

export default RoundButton
